import React, { useState } from "react";
import { Dices, Minus, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Keep in sync with QuestionsController.RandomPackageMin / RandomPackageMax.
export const RANDOM_PACKAGE_MIN = 12;
export const RANDOM_PACKAGE_MAX = 36;
export const RANDOM_PACKAGE_DEFAULT = 24;
const PRESETS = [12, 18, 24, 36];

export const clampRandomPackageCount = (n: number) =>
  Math.min(RANDOM_PACKAGE_MAX, Math.max(RANDOM_PACKAGE_MIN, Math.round(n)));

interface RandomPackageCountPickerProps {
  initial?: number;
  onSubmit: (count: number) => void;
}

/** Question-count selector (12–36) shared by the home card dialog and the random package page. */
const RandomPackageCountPicker: React.FC<RandomPackageCountPickerProps> = ({ initial, onSubmit }) => {
  const [count, setCount] = useState(clampRandomPackageCount(initial ?? RANDOM_PACKAGE_DEFAULT));

  const step = (delta: number) => setCount((c) => clampRandomPackageCount(c + delta));

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Sual sayını təyin edin. Suallar NHN paketlərindən təsadüfi seçiləcək
        ({RANDOM_PACKAGE_MIN}–{RANDOM_PACKAGE_MAX}).
      </p>

      <div className="flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => step(-1)}
          disabled={count <= RANDOM_PACKAGE_MIN}
          aria-label="Azalt"
          className="h-10 w-10 rounded-full border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Minus className="h-4 w-4" />
        </button>
        <div className="w-20 text-center">
          <span className="text-5xl font-black tabular-nums text-violet-600 dark:text-violet-300">{count}</span>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">sual</p>
        </div>
        <button
          type="button"
          onClick={() => step(1)}
          disabled={count >= RANDOM_PACKAGE_MAX}
          aria-label="Artır"
          className="h-10 w-10 rounded-full border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <input
        type="range"
        min={RANDOM_PACKAGE_MIN}
        max={RANDOM_PACKAGE_MAX}
        value={count}
        onChange={(e) => setCount(clampRandomPackageCount(Number(e.target.value)))}
        className="w-full accent-violet-600"
        aria-label="Sual sayı"
      />

      <div className="grid grid-cols-4 gap-2">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setCount(p)}
            className={cn(
              "rounded-lg border py-1.5 text-sm font-semibold transition-colors",
              count === p
                ? "border-violet-500 bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300"
                : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            )}
          >
            {p}
          </button>
        ))}
      </div>

      <Button onClick={() => onSubmit(count)} className="w-full gap-2 bg-violet-600 hover:bg-violet-700 text-white">
        <Dices className="h-4 w-4" />
        Paketi yarat
      </Button>
    </div>
  );
};

export default RandomPackageCountPicker;
